import { useEffect, useState } from "react";
import { ChartLineUp } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { QuoteCard, KindFilter } from "@/components/MarketCards";

export default function MarketsPublic() {
  const [kind, setKind] = useState("all");
  const [data, setData] = useState(null);

  useSeo({
    title: "World markets — share indices, government bond yields and currency rates | Unity & Utility",
    description: "A plain board of world share indices, government bond yields and currency rates for people who import parts, run vans and buy stock — updated through the day.",
    path: "/markets",
    jsonLd: breadcrumbs([["Home", "/"], ["Markets", "/markets"]]),
  });

  useEffect(() => {
    setData(null);
    api.get(`/markets/public/watch${kind === "all" ? "" : `?kind=${kind}`}`)
      .then(({ data: d }) => setData(d)).catch(() => setData({ instruments: [] }));
  }, [kind]);

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="markets-public-page">
        <Crumbs items={[["Home", "/"], ["Markets", null]]} />
        <p className="overline text-[#B3241B]">World markets</p>
        <h1 className="font-display mt-2 text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl">
          Shares, government bonds and currencies
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
          Where the world's share indices sit, what government paper is yielding and where the
          currencies are — the handful of numbers that actually move the cost of importing parts,
          running vans and funding stock.
        </p>
        <div className="mt-6">
          <ShareBar url={`${window.location.origin}/markets`} text="World markets board from Unity & Utility" testid="markets-public-share" compact />
        </div>

        <div className="mt-6"><KindFilter value={kind} onChange={setKind} testid="markets-public-kinds" /></div>

        {!data ? (
          <p className="py-16 text-center text-sm text-slate-500">Loading the board…</p>
        ) : data.instruments.length === 0 ? (
          <p data-testid="markets-public-empty" className="mt-8 border border-border bg-white p-8 text-center text-sm text-slate-600">
            <ChartLineUp size={20} className="mx-auto mb-2 text-slate-400" /> Nothing on the board yet.
          </p>
        ) : (
          <>
            {data.instruments.every((row) => row.value === null || row.value === undefined) && (
              <p data-testid="markets-public-pending" className="mt-6 border border-border bg-slate-50 px-4 py-3 text-sm text-slate-600">
                The board refreshes each morning — today's prices are not in yet. Check back shortly.
              </p>
            )}
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="markets-public-cards">
              {data.instruments.map((row, i) => <QuoteCard key={row.id} row={row} index={i} canEdit={false} />)}
            </div>
          </>
        )}
      </div>
    </PublicShell>
  );
}
